import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Search, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import './index.scss';

const MultiSelectDropdown = ({
  dropdownLabel,
  options = [],
  value = [],
  isMultiSelect = true,
  multipleLabel = 'Multiple',
  searchPlaceholder = 'Search',
  clearLabel = 'Clear Selected',
  cancelLabel = 'Cancel',
  applyLabel = 'Apply',
  noResultsLabel = 'No results found',
  onApply = undefined,
  onCancel = undefined,
  onSearch = undefined,
  searchDebounceMs = 500,
  isSearching = false,
  menuPaperClassName = '',
  className = '',
  textField,
  valueField,
  renderCustomOptions,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [pendingValues, setPendingValues] = useState(value || []);
  const debounceTimerRef = useRef(null);

  // Debounced search handler for async search
  const handleSearchChange = useCallback((event) => {
    const newValue = event.target.value;
    setSearchValue(newValue);

    // If onSearch is provided, use debounced async search
    if (onSearch) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        onSearch(newValue.trim());
      }, searchDebounceMs);
    }
  }, [onSearch, searchDebounceMs]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);


  // Sync pendingValues with value prop only when menu closes
  // Using JSON.stringify to avoid infinite loops from array reference changes
  const valueString = JSON.stringify(value || []);
  useEffect(() => {
    if (!anchorEl) {
      setPendingValues(JSON.parse(valueString));
    }
  }, [anchorEl, valueString]);

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => (a?.[textField || 'label'] || '').localeCompare(b?.[textField || 'label'] || ''));
  }, [options, textField]);

  // For async search (onSearch provided), use options directly (already filtered by API)
  // For client-side search, filter locally
  const filteredOptions = useMemo(() => {
    if (onSearch) {
      // Async mode: options are already filtered by API, just sort them
      return sortedOptions;
    }

    // Client-side filtering mode
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) {
      return sortedOptions;
    }

    return sortedOptions.filter((option) => option?.[textField || 'label']?.toLowerCase().includes(normalizedSearch));
  }, [searchValue, sortedOptions, onSearch, textField]);

  const hasNoSearchResults = useMemo(() => {
    if (renderCustomOptions) return false;
    return searchValue.trim().length > 0 && filteredOptions.length === 0;
  }, [searchValue, filteredOptions, renderCustomOptions]);

  const triggerLabel = useMemo(() => {
    if (!value || value.length === 0) {
      return dropdownLabel;
    }

    if (value.length === 1) {
      const selectedOption = options.find((option) => option?.[valueField || 'value'] === value[0]);
      return selectedOption?.[textField || 'label'] || dropdownLabel;
    }

    return multipleLabel;
  }, [dropdownLabel, multipleLabel, options, value, valueField, textField]);

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setSearchValue('');
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSearchValue('');
  };

  const toggleOption = (optionValue) => {
    setPendingValues((prev) => {
      if (!isMultiSelect) {
        return prev[0] === optionValue ? [] : [optionValue];
      }

      return prev.includes(optionValue)
        ? prev.filter((current) => current !== optionValue)
        : [...prev, optionValue];
    });
  };

  const handleApply = () => {
    const uniqueValues = Array.from(new Set(pendingValues));
    onApply?.(uniqueValues);
    closeMenu();
  };

  const handleClear = () => {
    setPendingValues([]);
  };

  const handleCancel = () => {
    setPendingValues(value);
    closeMenu();
    onCancel?.();
  };

  return (
    <div className={`multi-select-dropdown ${className || ''}`}>
      <Typography
        variant='caption'
        className='dropdown-label'
      >
        {dropdownLabel}
      </Typography>
      <button
        type='button'
        className='dropdown-trigger'
        onClick={openMenu}
      >
        <span>{triggerLabel}</span>
        {anchorEl ? <KeyboardArrowUp className='dropdown-trigger-icon' /> : <KeyboardArrowDown className='dropdown-trigger-icon' />}
      </button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ disablePadding: true }}
        PaperProps={{ className: menuPaperClassName || 'dropdown-menu-paper' }}
      >
        <Box className='dropdown-menu' onClick={(event) => event.stopPropagation()}>
          <TextField
            fullWidth
            size='small'
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={handleSearchChange}
            className='search-field'
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  {isSearching ? <CircularProgress size={20} /> : <Search fontSize='small' />}
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant='contained'
            color='primary'
            fullWidth
            size='small'
            className='clear-button'
            onClick={handleClear}
            disabled={pendingValues.length === 0}
          >
            {clearLabel}
          </Button>
          <Box className='dropdown-options'>
            {renderCustomOptions
              ? renderCustomOptions({
                searchValue,
                pendingValues,
                toggleOption,
                setPendingValues,
                options,
              })
              : (filteredOptions.length === 0
                ? (<Typography variant='body2' color='text.secondary'>
                  {noResultsLabel}
                </Typography>)
                : (filteredOptions.map((option) => (
                  <FormControlLabel
                    key={option?.[valueField || 'value']}
                    control={
                      <Checkbox
                        checked={pendingValues.includes(option?.[valueField || 'value'])}
                        onChange={() => toggleOption(option?.[valueField || 'value'])}
                        color='primary'
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {option.icon && (
                          <Box component='img' src={option.icon} alt='' sx={{ width: 16, height: 16 }} />
                        )}
                        <span>{option?.[textField || 'label']}</span>
                      </Box>
                    }
                  />
                )))
              )
            }
          </Box>
          <Stack direction='row' spacing={1} className='dropdown-actions'>
            <Button fullWidth variant='outlined' onClick={handleCancel}>
              {cancelLabel}
            </Button>
            <Button fullWidth variant='contained' onClick={handleApply} disabled={hasNoSearchResults}>
              {applyLabel}
            </Button>
          </Stack>
        </Box>
      </Menu>
    </div>
  );
};

MultiSelectDropdown.propTypes = {
  dropdownLabel: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      icon: PropTypes.string,
    }),
  ),
  value: PropTypes.arrayOf(PropTypes.string),
  isMultiSelect: PropTypes.bool,
  multipleLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  clearLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  applyLabel: PropTypes.string,
  noResultsLabel: PropTypes.string,
  onApply: PropTypes.func,
  onCancel: PropTypes.func,
  onSearch: PropTypes.func,
  searchDebounceMs: PropTypes.number,
  isSearching: PropTypes.bool,
  className: PropTypes.string,
  menuPaperClassName: PropTypes.string,
  textField: PropTypes.string,
  valueField: PropTypes.string,
  renderCustomOptions: PropTypes.func,
};

export default MultiSelectDropdown;
